import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { groupBy } from 'lodash'
import { ProductTypeEnum, MembershipType, PlatformName } from '__generatedTypes__/globalTypes'
import { findAllProduct, findAllProductVariables } from '__generatedTypes__/findAllProduct'
import { FIND_ALL_PRODUCT } from 'queries/products'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { getMembershipTitle } from 'utils/getMembershipTitle'

const MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE: Record<MembershipType, ProductTypeEnum> = {
  [MembershipType.Free]: ProductTypeEnum.Free,
  [MembershipType.Premium]: ProductTypeEnum.Premium,
  [MembershipType.PremiumMax]: ProductTypeEnum.PremiumMax,
} as const

const getMembershipTypeFromProductType = (productType: ProductTypeEnum): MembershipType =>
  Object.keys(MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE).find(
    (key) => MAP_MEMBERSHIP_TYPE_TO_PRODUCT_TYPE[key as MembershipType] === productType
  ) as MembershipType

export function useMemberships() {
  const { data: productsData, loading: productsLoading } = useQuery<findAllProduct, findAllProductVariables>(
    FIND_ALL_PRODUCT,
    {
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
        },
      },
    }
  )

  const products = useMemo(() => {
    const nonFreeProducts = productsData?.findAllProduct.items?.filter((product) =>
      [ProductTypeEnum.Premium, ProductTypeEnum.PremiumMax].includes(product.type)
    )

    if (!nonFreeProducts?.length) {
      return {}
    }

    return groupBy(nonFreeProducts, (product) => product.platform.title)
  }, [productsData])

  const getMembershipItems = (platformTitle: PlatformName) => {
    const platformProducts = products[platformTitle] || []

    return platformProducts.map((product) => ({
      id: product.id,
      title: getMembershipTitle(product),
    }))
  }

  const getProductById = (productId: string) => {
    const platformProducts = Object.values(products).flat()
    return platformProducts.find(({ id }) => id === productId)
  }

  const getMembershipTypeFromProduct = (productId: string) => {
    const product = getProductById(productId)
    return product ? getMembershipTypeFromProductType(product.type) : null
  }

  const getDurationFromProduct = (productId: string) => {
    const product = getProductById(productId)
    return product?.duration || null
  }

  return {
    products,
    loading: productsLoading,
    getMembershipItems,
    getProductById,
    getMembershipTypeFromProduct,
    getDurationFromProduct,
  }
}
